// Booking Page — Multi-step booking flow with Firestore
import { renderHeader, renderBottomNav } from '../components/header.js';
import { createCalendar, formatThaiDate } from '../components/calendar.js';
import { toastSuccess, toastError } from '../components/toast.js';
import { getFortuneTeller, getAvailableDates, getAvailableSlots, addDemoBooking } from '../data/demo-store.js';
import { getCurrentUser } from '../auth.js';
import { navigate } from '../router.js';
import { isFirebaseReady } from '../firebase.js';
import { createBooking, getAdminSettingsDB, getFortuneTellerDB, getAvailableDatesDB, getAvailableSlotsDB, bookSlotDB, getUser, updateUserProfileDB } from '../services/db.js';
import { t } from '../i18n.js';
import { notifyNewBooking } from '../services/api.js';

// Helper: Resize Image Client-side to avoid Firebase Storage & stay under Firestore 1MB limits
export function resizeImageToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                const MAX_SIZE = 600;
                if (width > height) {
                    if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; }
                } else {
                    if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/jpeg', 0.8));
            };
            img.onerror = () => reject(new Error('ไฟล์ภาพไม่ถูกต้อง'));
            img.src = e.target.result;
        };
        reader.onerror = () => reject(new Error('อ่านไฟล์ล้มเหลว'));
        reader.readAsDataURL(file);
    });
}
const SERVICE_TYPES = [
  { id: 'onsite', icon: '🏠', get label() { return t('service_onsite'); }, desc: '' },
  { id: 'chat', icon: '💬', get label() { return t('service_chat'); }, desc: '' },
  { id: 'call', icon: '📞', get label() { return t('service_call'); }, desc: '' },
  { id: 'video', icon: '📹', get label() { return t('service_video'); }, desc: '' }
];

let state = { ftId: null, step: 1, serviceType: null, date: null, time: null };

export async function renderBooking(params) {
  const ftId = params.id;
  // Load from Firestore or demo
  const ft = isFirebaseReady() ? await getFortuneTellerDB(ftId) : getFortuneTeller(ftId);
  if (!ft) { navigate('/home'); return; }
  state.ftId = ftId;
  state.ft = ft; // Cache for use in step renders

  const app = document.getElementById('app');
  app.innerHTML = `
    ${renderHeader()}
    <div class="page">
      <div class="flex gap-md mb-lg" style="align-items:center;">
        <img class="card-avatar" src="${ft.pictureUrl || 'https://ui-avatars.com/api/?name= FT &background=9b59f0&color=fff&size=56'}" alt="${ft.name}"
             onerror="this.onerror=null; this.src='https://ui-avatars.com/api/?name= FT &background=9b59f0&color=fff&size=56'" />
        <div>
          <h2 style="font-size: var(--font-xl); font-weight:700;">${ft.name}</h2>
          <p class="text-sm text-muted">${ft.specialty || ''}</p>
        </div>
      </div>
      <div class="steps">
        <div class="step ${state.step >= 1 ? 'active' : ''} ${state.step > 1 ? 'completed' : ''}">1</div>
        <div class="step-line ${state.step > 1 ? 'completed' : ''}"></div>
        <div class="step ${state.step >= 2 ? 'active' : ''} ${state.step > 2 ? 'completed' : ''}">2</div>
        <div class="step-line ${state.step > 2 ? 'completed' : ''}"></div>
        <div class="step ${state.step >= 3 ? 'active' : ''} ${state.step > 3 ? 'completed' : ''}">3</div>
        <div class="step-line ${state.step > 3 ? 'completed' : ''}"></div>
        <div class="step ${state.step >= 4 ? 'active' : ''}">4</div>
      </div>
      <div id="booking-step-content"><div style="text-align:center;padding:var(--space-lg);"><div class="loading-spinner"></div></div></div>
    </div>
    ${renderBottomNav('home')}
  `;
  await renderCurrentStep(ft);
}

async function renderCurrentStep(ft) {
  const c = document.getElementById('booking-step-content');
  if (!c) return;
  switch (state.step) {
    case 1: renderStepService(c, ft); break;
    case 2: await renderStepDate(c, ft); break;
    case 3: await renderStepTime(c, ft); break;
    case 4: await renderStepConfirm(c, ft); break;
  }
}

function renderStepService(c, ft) {
  const services = Array.isArray(ft.services) && ft.services.length > 0 
      ? ft.services 
      : ['ดูดวงทั่วไป'];

  c.innerHTML = `
    <h3 class="mb-lg" style="color: var(--accent-gold);">${t('booking_step1_title')}</h3>
    <p class="text-muted mb-lg">${t('booking_step1_desc')}</p>
    <div class="service-options">
      ${services.map(s => `
        <button class="service-option ${state.serviceType === s ? 'selected' : ''}" data-service="${s}">
          <span class="service-icon">🔮</span>
          <span class="service-label" style="text-align:left; flex:1;">${s}</span>
        </button>
      `).join('')}
    </div>
    <div class="mt-lg">
      <button class="btn btn-primary btn-block" id="next-step" ${!state.serviceType ? 'disabled' : ''}>${t('btn_next')}</button>
    </div>
  `;
  c.querySelectorAll('.service-option').forEach(btn => {
    btn.addEventListener('click', () => { state.serviceType = btn.dataset.service; renderBooking({ id: state.ftId }); });
  });
  document.getElementById('next-step')?.addEventListener('click', () => { if (state.serviceType) { state.step = 2; renderBooking({ id: state.ftId }); } });
}

async function renderStepDate(c, ft) {
  const availDates = isFirebaseReady() ? await getAvailableDatesDB(ft.id) : getAvailableDates(ft.id);
  c.innerHTML = `
    <h3 class="mb-lg" style="color: var(--accent-gold);">${t('booking_step2_title')}</h3>
    <div id="calendar-container"></div>
    <div class="mt-lg flex gap-sm">
      <button class="btn btn-outline btn-block" id="prev-step">${t('btn_back')}</button>
      <button class="btn btn-primary btn-block" id="next-step" ${!state.date ? 'disabled' : ''}>${t('btn_next')}</button>
    </div>
  `;
  createCalendar('calendar-container', {
    availableDates: availDates, selectedDate: state.date,
    onDateSelect: (d) => { state.date = d; const b = document.getElementById('next-step'); if (b) b.disabled = false; }
  });
  document.getElementById('prev-step')?.addEventListener('click', () => { state.step = 1; renderBooking({ id: state.ftId }); });
  document.getElementById('next-step')?.addEventListener('click', () => { if (state.date) { state.step = 3; renderBooking({ id: state.ftId }); } });
}

async function renderStepTime(c, ft) {
  const slots = isFirebaseReady() ? await getAvailableSlotsDB(ft.id, state.date) : getAvailableSlots(ft.id, state.date);
  c.innerHTML = `
    <h3 class="mb-md" style="color: var(--accent-gold);">${t('booking_step2_title')} (Time)</h3>
    <p class="text-sm text-muted mb-lg">📅 ${formatThaiDate(state.date)}</p>
    ${slots.length > 0 ? `
      <div class="time-slots">
        ${slots.map(s => `<button class="time-slot ${state.time === s ? 'selected' : ''}" data-time="${s}">${s}</button>`).join('')}
      </div>
    ` : `<div class="empty-state"><div class="empty-state-icon">😔</div><div class="empty-state-title">Fully Booked / ไม่มีเวลาว่าง</div></div>`}
    <div class="mt-lg flex gap-sm">
      <button class="btn btn-outline btn-block" id="prev-step">${t('btn_back')}</button>
      <button class="btn btn-primary btn-block" id="next-step" ${!state.time ? 'disabled' : ''}>${t('btn_next')}</button>
    </div>
  `;
  c.querySelectorAll('.time-slot').forEach(btn => {
    btn.addEventListener('click', () => { state.time = btn.dataset.time; renderBooking({ id: state.ftId }); });
  });
  document.getElementById('prev-step')?.addEventListener('click', () => { state.step = 2; renderBooking({ id: state.ftId }); });
  document.getElementById('next-step')?.addEventListener('click', () => { if (state.time) { state.step = 4; renderBooking({ id: state.ftId }); } });
}

async function renderStepConfirm(c, ft) {
  const serviceLabel = state.serviceType;
  const adminSettings = isFirebaseReady() ? await getAdminSettingsDB() : null;
  const user = getCurrentUser();
  const userDb = isFirebaseReady() ? await getUser(user.id) : null;
  const hasPhone = userDb?.phone && userDb.phone.trim().length >= 9;

  // Parse existing user dob/tob to pre-select
  let uDay = '', uMonth = '', uYear = '', uHour = '', uMin = '';
  if (userDb?.dob && userDb.dob.includes('-')) {
      const parts = userDb.dob.split('-'); // YYYY-MM-DD
      uYear = parseInt(parts[0]) + 543; 
      uMonth = parseInt(parts[1]);
      uDay = parseInt(parts[2]);
  }
  if (userDb?.tob && userDb.tob.includes(':')) {
      const parts = userDb.tob.split(':');
      uHour = parseInt(parts[0]);
      uMin = parseInt(parts[1]);
  }
  
  const currentYearBE = new Date().getFullYear() + 543;
  const yearOptions = Array.from({length: 151}, (_, i) => { 
      const be = currentYearBE + 50 - i;  // allow +50 to -100 years from now
      const ce = be - 543; 
      return `<option value="${be}" ${uYear === be ? 'selected' : ''}>${be} / ${ce}</option>`; 
  }).join('');

  c.innerHTML = `
    <h3 class="mb-lg" style="color: var(--accent-gold);">${t('booking_step3_title')}</h3>
    <div class="card" style="background: var(--gradient-card);">
      <div class="card-body">
        <div style="display:grid; gap: var(--space-md);">
          <div class="flex-between"><span class="text-muted">${t('booking_lbl_service')}</span><span>🔮 ${serviceLabel}</span></div>
          <div class="flex-between"><span class="text-muted">${t('booking_lbl_datetime')}</span><span>${formatThaiDate(state.date)} ${state.time}</span></div>
        </div>
      </div>
    </div>
    <!-- 🌸 Pricing Info Banner (Dynamic from Admin Settings) -->
    <div class="card mt-lg" style="background: linear-gradient(135deg, rgba(155,89,240,0.08), rgba(233,77,158,0.06)); border: 1px solid rgba(155,89,240,0.2);">
      <div class="card-body" style="padding:var(--space-md);">
        <p style="color:var(--accent-gold); font-weight:700; font-size:var(--font-md); margin-bottom:var(--space-sm);">🌸 อัตราค่าครูดูดวง (กรุณาเลือก)</p>
        <p style="color:var(--text-secondary); font-size:var(--font-xs); margin-bottom:var(--space-sm); line-height:1.4;">
          ค่าครูจะคำนวณตามข้อมูลเวลาเกิด กรุณาเลือกรูปแบบที่ตรงกับคุณ
        </p>
        <div style="display:flex; flex-direction:column; gap:8px; font-size:var(--font-sm);">
          ${(adminSettings?.pricing_tiers || [
            { label: 'มีเวลาเกิด (เช่น 8.45 น.)', price: 599, color: '#06C755' },
            { label: 'ไม่มีเวลาเกิด+ไม่มั่นใจนาที', price: 999, color: '#f2994a' },
            { label: 'มีฝาแฝด', price: 999, color: '#f2994a', unit: '/คน' },
            { label: 'เกิดต่างประเทศ มีเวลาเกิด', price: 999, color: '#9b59f0' },
            { label: 'เกิดต่างประเทศ ไม่มีเวลาเกิด', price: 1599, color: '#e94d9e' }
          ]).map((tier, index) => `
          <label style="display:flex; justify-content:space-between; align-items:center; cursor:pointer; padding:8px 12px; border:1px solid rgba(0,0,0,0.1); border-radius:8px; background:#fff; transition:all 0.2s;">
            <div style="display:flex; align-items:center; gap:8px;">
               <input type="radio" name="price_tier" value="${index}" data-price="${tier.price}" data-label="${tier.label}" style="accent-color:var(--accent-gold); transform:scale(1.2);">
               <span style="color:var(--text-secondary); font-weight:500;">🌸 ${tier.label}</span>
            </div>
            <span style="color:${tier.color || '#06C755'}; font-weight:700;">${tier.price.toLocaleString()} บ.${tier.unit || ''}</span>
          </label>
          `).join('')}
        </div>
      </div>
    </div>

    <!-- Astrology Data Form -->
    <div class="card mt-lg" style="background: var(--surface);">
      <div class="card-body">
        <h3 class="mb-md" style="color: var(--accent-gold);">📜 ข้อมูลดวงชะตา (กรุณากรอกให้ครบถ้วน)</h3>
        
        <div class="form-group ${hasPhone ? 'hidden' : ''}">
            <label class="form-label">เบอร์โทรศัพท์ <span class="text-danger">*</span></label>
            <input type="tel" id="booking-phone" class="form-input" placeholder="08xxxxxxxx" value="${hasPhone ? userDb.phone : ''}">
        </div>

        <div class="form-group">
            <label class="form-label">วัน/เดือน/ปี เกิด <span class="text-danger">*</span></label>
            <div style="display:flex; gap:8px;">
               <select id="booking-dob-day" class="form-select" style="flex:1; padding-left: 8px;">
                  <option value="" disabled ${!uDay ? 'selected' : ''}>วัน</option>
                  ${Array.from({length: 31}, (_, i) => `<option value="${i+1}" ${uDay === i+1 ? 'selected' : ''}>${String(i+1).padStart(2, '0')}</option>`).join('')}
               </select>
               <select id="booking-dob-month" class="form-select" style="flex:2; padding-left: 8px;">
                  <option value="" disabled ${!uMonth ? 'selected' : ''}>เดือน</option>
                  ${['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'].map((m, i) => `<option value="${i+1}" ${uMonth === i+1 ? 'selected' : ''}>${m} (${String(i+1).padStart(2, '0')})</option>`).join('')}
               </select>
               <select id="booking-dob-year" class="form-select" style="flex:2; padding-left: 8px;">
                  <option value="" disabled ${!uYear ? 'selected' : ''}>ปี พ.ศ./ค.ศ.</option>
                  ${yearOptions}
               </select>
            </div>
        </div>
        <div class="form-group">
            <label class="form-label">เวลาเกิด</label>
            <div style="display:flex; gap:8px; align-items:center;">
               <select id="booking-tob-hour" class="form-select" style="flex:1; padding-left: 8px;">
                  <option value="" ${uHour === '' ? 'selected' : ''}>ชั่วโมง</option>
                  ${Array.from({length: 24}, (_, i) => `<option value="${i}" ${uHour === i ? 'selected' : ''}>${String(i).padStart(2, '0')}</option>`).join('')}
               </select>
               <span style="font-weight:bold;">:</span>
               <select id="booking-tob-minute" class="form-select" style="flex:1; padding-left: 8px;">
                  <option value="" ${uMin === '' ? 'selected' : ''}>นาที</option>
                  ${Array.from({length: 60}, (_, i) => `<option value="${i}" ${uMin === i ? 'selected' : ''}>${String(i).padStart(2, '0')}</option>`).join('')}
               </select>
               <span style="color:var(--text-secondary); font-size: 14px;">น.</span>
               <button type="button" class="btn btn-outline" id="btn-time-now" style="padding: 0 10px; height: 42px; font-size: 12px; border-radius: 8px;">ตอนนี้</button>
            </div>
            <div class="text-xs text-muted mt-xs">⏰ กรอกให้ตรงที่สุด หากไม่ทราบให้เว้นว่างไว้</div>
        </div>
        <div class="form-group">
            <label class="form-label">ส่วนสูง (ซม.) และ เพศ <span class="text-danger">*</span></label>
            <div style="display:flex; gap:10px;">
              <div style="flex:1; position:relative; display:flex; align-items:center;">
                <input type="text" id="booking-height" class="form-input" placeholder="เช่น 165" value="${userDb?.height || ''}" style="width:100%; padding-right:48px;">
                <span style="position:absolute; right:10px; font-size:var(--font-xs); color:var(--text-secondary); pointer-events:none;">ซม.</span>
              </div>
              <select id="booking-gender" class="form-select" style="flex:1;">
                <option value="" disabled ${!userDb?.gender ? 'selected' : ''}>-- เลือกเพศ --</option>
                <option value="หญิง" ${userDb?.gender === 'หญิง' ? 'selected' : ''}>หญิง</option>
                <option value="ชาย" ${userDb?.gender === 'ชาย' ? 'selected' : ''}>ชาย</option>
              </select>
            </div>
        </div>
        <div class="form-group">
            <label class="form-label">สถานที่เกิด (จังหวัด/ประเทศ) <span class="text-danger">*</span></label>
            <input type="text" id="booking-pob" class="form-input" placeholder="บอกจังหวัด หรือ ประเทศ" value="${userDb?.pob || ''}">
        </div>
        <div class="form-group">
            <label class="form-label">คำถามที่ต้องการถาม</label>
            <textarea id="booking-question" class="form-textarea" rows="3" placeholder="ระบุเรื่องที่กำลังสนใจหรือปัญหาที่อยากให้ดู (ไม่บังคับ)"></textarea>
        </div>
        <div class="form-group">
            <label class="form-label">รูปภาพหน้าตรง (ชัดเจน) <span class="text-danger">*</span></label>
            <p class="text-xs text-muted mb-sm">ไม่ใช้แอพปรับรูป ใส่ได้ 1 - 3 รูป</p>
            <div class="file-upload" id="face-upload-area" onclick="document.getElementById('face-file-input').click()" style="cursor:pointer; padding:15px; border:2px dashed var(--accent-gold); border-radius:8px; text-align:center; background: rgba(155, 89, 240, 0.03);">
              <div class="file-upload-icon mb-xs" id="face-upload-icon" style="font-size:24px;">📸</div>
              <div class="file-upload-text" id="face-upload-text" style="color:var(--accent-gold); font-size:14px;">
                  แตะเพื่อเลือกรูปภาพ (เลือกได้สูงสุด 3 รูป)
              </div>
              <input type="file" id="face-file-input" accept="image/*" multiple style="display:none;" />
            </div>
        </div>


      </div>
    </div>

    <div class="mt-lg flex gap-sm">
      <button class="btn btn-outline btn-block" id="prev-step">${t('btn_back')}</button>
      <button class="btn btn-gold btn-block" id="confirm-booking">${t('btn_confirm_booking')}</button>
    </div>
  `;

  // Attach Image Preview Listener
  document.getElementById('face-file-input')?.addEventListener('change', (e) => {
      const files = e.target.files;
      if (files.length > 0) {
          if (files.length > 3) {
              toastError('เลือกรูปภาพได้สูงสุด 3 รูปเท่านั้นครับ');
              e.target.value = ''; // Reset input
              document.getElementById('face-upload-text').textContent = 'แตะเพื่อเลือกรูปภาพ (เลือกได้สูงสุด 3 รูป)';
              document.getElementById('face-upload-text').style.color = 'var(--accent-gold)';
              return;
          }
          const text = files.length === 1 ? files[0].name : `เลือกแล้ว ${files.length} รูป`;
          document.getElementById('face-upload-text').textContent = '📸 ' + text;
          document.getElementById('face-upload-text').style.color = '#06C755';
      } else {
          document.getElementById('face-upload-text').textContent = 'แตะเพื่อเลือกรูปภาพ (เลือกได้สูงสุด 3 รูป)';
          document.getElementById('face-upload-text').style.color = 'var(--accent-gold)';
      }
  });

  document.getElementById('btn-time-now')?.addEventListener('click', () => {
      const now = new Date();
      document.getElementById('booking-tob-hour').value = now.getHours();
      document.getElementById('booking-tob-minute').value = now.getMinutes();
  });

  document.getElementById('prev-step')?.addEventListener('click', () => { state.step = 3; renderBooking({ id: state.ftId }); });
  document.getElementById('confirm-booking')?.addEventListener('click', async () => {
    
    const selectedRadio = document.querySelector('input[name="price_tier"]:checked');
    if (!selectedRadio) {
      alert('กรุณาเลือกอัตราค่าครูดูดวงก่อนยืนยัน');
      return;
    }

    const btn = document.getElementById('confirm-booking');
    btn.disabled = true;
    btn.textContent = '⏳ ...';

    const price = parseInt(selectedRadio.dataset.price, 10);
    const tierLabel = selectedRadio.dataset.label;
    const note = document.getElementById('booking-note')?.value || '';

    // Collect Astrology Data
    const phone = document.getElementById('booking-phone')?.value || userDb?.phone;
    
    const dDay = document.getElementById('booking-dob-day')?.value;
    const dMonth = document.getElementById('booking-dob-month')?.value;
    const dYear = document.getElementById('booking-dob-year')?.value;
    let dob = '';
    if (dDay && dMonth && dYear) {
        const ceYear = parseInt(dYear) - 543;
        dob = `${ceYear}-${String(dMonth).padStart(2, '0')}-${String(dDay).padStart(2, '0')}`;
    }

    const tHour = document.getElementById('booking-tob-hour')?.value;
    const tMin = document.getElementById('booking-tob-minute')?.value;
    let tob = '';
    if (tHour !== '' && tMin !== '') {
        tob = `${String(tHour).padStart(2, '0')}:${String(tMin).padStart(2, '0')}`;
    }

    const height = document.getElementById('booking-height')?.value;
    const gender = document.getElementById('booking-gender')?.value;
    const pob = document.getElementById('booking-pob')?.value;
    const question = document.getElementById('booking-question')?.value;
    const faceFiles = document.getElementById('face-file-input')?.files;

    if (!dob || !height || !gender || !pob || (!hasPhone && !phone)) {
        toastError('กรุณากรอกข้อมูลดวงชะตาให้ครบถ้วน');
        btn.disabled = false;
        btn.textContent = t('btn_confirm_booking');
        return;
    }

    if (!faceFiles || faceFiles.length === 0) {
        toastError('กรุณาแนบรูปภาพหน้าตรงอย่างน้อย 1 รูป');
        btn.disabled = false;
        btn.textContent = t('btn_confirm_booking');
        return;
    }

    btn.textContent = '⏳ กำลังเตรียมข้อมูล...';

    // Compress Images
    let photoBase64Array = [];
    try {
        for (let i = 0; i < faceFiles.length; i++) {
             const b64 = await resizeImageToBase64(faceFiles[i]);
             photoBase64Array.push(b64);
        }
    } catch(err) {
        toastError('ไฟล์ภาพไม่ถูกต้อง กรุณาอัปโหลดใหม่');
        btn.disabled = false;
        btn.textContent = t('btn_confirm_booking');
        return;
    }

    const detailsObj = { 
        phone, dob, tob, height, gender, pob, question,
        birth_confidence: selectedRadio.value || 'has_time',
        face_photo_url: photoBase64Array[0],
        face_photo_urls: photoBase64Array
    };

    btn.textContent = '⏳ กำลังบันทึกการจอง...';

    const bookingData = {
      user_id: user.id, customer_name: user.name,
      fortune_teller_id: ft.id, fortune_teller_name: ft.name,
      service_type: state.serviceType, date: state.date, time: state.time,
      price: price, price_tier: tierLabel, note,
      customer_details: detailsObj
    };

    let booking;
    if (isFirebaseReady()) {
      booking = await createBooking(bookingData);
      // Mark this slot as booked in the schedule
      await bookSlotDB(ft.id, state.date, state.time);
      
      // Update User Profile with the newly entered details
      if (user.id) {
          await updateUserProfileDB(user.id, { phone, dob, tob, height, gender, pob });
      }

      // Trigger Webhook for Google Calendar Sync
      try {
        const settings = await getAdminSettingsDB();
        
        // --- 1. Send LIFF message to user's chat ---
        if (window.liff && window.liff.isInClient && window.liff.isInClient()) {
          const chatMsg = `✨ ส่งคำขอจองสำเร็จ!\n\n📋 บริการ: ${state.serviceType}\n📅 วัน-เวลา: ${formatThaiDate(state.date)} ${state.time} น.\n\n⏳ กรุณารอแอดมินยืนยันคิวการจอง ก่อนโอนเงิน`;
          
          window.liff.sendMessages([
            { type: 'text', text: chatMsg }
          ]).catch(e => console.error('LIFF message failed:', e));
        }

        // --- 3. Send automated notifications via cPanel Backend ---
        await notifyNewBooking(booking);
        
      } catch (err) {
        console.error('Error in post-booking sequence:', err);
      }
    } else {
      booking = addDemoBooking(bookingData);
    }

    state = { ftId: null, step: 1, serviceType: null, date: null, time: null };
    const serviceMock = { label: booking.service_type, icon: '🔮' };
    showBookingSuccess(booking, ft, serviceMock);
  });
}

async function showBookingSuccess(booking, ft, service) {
  const app = document.getElementById('app');
  app.innerHTML = `
    ${renderHeader()}
    <div class="page">
      <div style="text-align:center; margin-bottom: var(--space-xl);">
        <div style="font-size:4rem; margin-bottom: var(--space-sm);">✅</div>
        <h2 class="page-title">${t('booking_success_title')}</h2>
        <p class="text-sm text-muted">${t('booking_success_desc')}</p>
        
        <div style="margin-top:var(--space-md); padding:var(--space-md); background:rgba(6, 199, 85, 0.1); border:1px solid #06C755; border-radius:var(--radius-md);">
          <p style="color:#06C755; font-weight:600; font-size:var(--font-md); margin-bottom:var(--space-xs);">💬 รายละเอียดการชำระเงินส่งไปในไลน์เรียบร้อยแล้ว</p>
          <p class="text-sm" style="color:#06C755;">แล้วรอแอดมินยืนยัน ก่อนชำระเงิน นะคะ</p>
        </div>
      </div>
      <div class="card mb-lg" style="background: var(--gradient-card);">
        <h3 style="color: var(--accent-gold); margin-bottom: var(--space-md);">${t('booking_review_title')}</h3>
        <div class="card-body" style="display:grid; gap: var(--space-sm);">
          <div class="flex-between"><span class="text-muted">${t('booking_lbl_service')}</span><span>${service.icon} ${service.label}</span></div>
          <div class="flex-between"><span class="text-muted">${t('booking_lbl_datetime')}</span><span>${formatThaiDate(booking.date)} ${booking.time} น.</span></div>
          <div class="flex-between"><span class="text-muted">Status</span><span class="badge ${booking.status === 'pending' ? 'badge-pending' : 'badge-confirmed'}">${t('status_pending')}</span></div>
          <div class="divider"></div>
          <div class="flex-between"><span class="fw-600">${t('booking_lbl_price')}</span><span class="text-gold fw-700" style="font-size:var(--font-xl);">฿${(booking.price || 0).toLocaleString()}</span></div>
        </div>
      </div>

      <div class="flex gap-sm mt-lg">
        <button class="btn btn-outline btn-block" onclick="location.hash='#/history'">📋 ${t('history_title')}</button>
      </div>
    </div>
    ${renderBottomNav('home')}
  `;
}
